/**
 * useResolvedAddress
 * Layer: shared/hooks (no external deps beyond React)
 * Change: manejo-de-coordenadas-y-mapas
 *
 * Resolves a human-readable address from GPS coordinates via the cached
 * reverse-geocoding lookup. Never throws — resolution failures leave
 * address as null.
 */
import { useState, useEffect } from 'react';
import type { GeolocationCoords } from '@/shared/hooks/useGeolocation';
import { resolveAddressCached } from '@/shared/geocoding/geocodingCache';

export interface ResolvedAddressState {
  address: string | null;
  isLoading: boolean;
}

export function useResolvedAddress(coordinates: GeolocationCoords | null): ResolvedAddressState {
  const [state, setState] = useState<ResolvedAddressState>({
    address: null,
    isLoading: false,
  });

  useEffect(() => {
    if (!coordinates || (coordinates.lat === 0 && coordinates.lng === 0)) {
      setState({ address: null, isLoading: false });
      return;
    }

    let cancelled = false;
    setState({ address: null, isLoading: true });

    void (async () => {
      try {
        const result = await resolveAddressCached(coordinates.lat, coordinates.lng);
        if (!cancelled) {
          setState({ address: result?.formattedAddress ?? null, isLoading: false });
        }
      } catch {
        if (!cancelled) {
          setState({ address: null, isLoading: false });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [coordinates]);

  return state;
}
